import { memo, useRef, useCallback } from "react"
import { Box, IconButton, InputAdornment, TextField } from "@mui/material"
import { Search, X } from "lucide-react"

// Memoized search input component to prevent re-renders from affecting input focus
// This component is completely isolated from parent re-renders
interface SearchInputProps {
  placeholder: string
  onSearch: (value: string) => void
  onClear: () => void
  searchQuery: string
  debounceMs?: number
}

export const SearchInput = memo(function SearchInput({
  placeholder,
  onSearch,
  onClear,
  searchQuery,
  debounceMs = 500
}: SearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Handle input change with debounce
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set new timeout - only call onSearch after debounce
    timeoutRef.current = setTimeout(() => {
      onSearch(value)
    }, debounceMs)
  }, [onSearch, debounceMs])

  // Handle clear button click
  const handleClear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (inputRef.current) {
      inputRef.current.value = ""
    }
    onClear()
  }, [onClear])

  return (
    <Box sx={{ mb: 2 }}>
      <TextField
        fullWidth
        size="small"
        type="text"
        placeholder={placeholder}
        defaultValue=""
        onChange={handleChange}
        inputRef={inputRef}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={16} />
            </InputAdornment>
          ),
          endAdornment: searchQuery ? (
            <InputAdornment position="end">
              <IconButton type="button" size="small" onClick={handleClear} aria-label="Clear search" edge="end">
                <X size={16} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  )
})

export default SearchInput
